export interface MessageQueueOptions<T> {
  /** 实际处理每条消息的回调 */
  handler: (data: T) => void;
  /** 两次消息处理之间的最小间隔（ms），默认 50 */
  interval?: number;
  /** 队列排空完毕时的回调，配合 startDrain 使用 */
  onDrain?: () => void;
}

export interface MessageQueue<T> {
  /** 将消息入队，若队列空闲则立即开始按节奏消费 */
  push: (data: T) => void;
  /** 立即同步处理所有剩余消息（跳过定时器），处理完触发 onDrain */
  flush: () => void;
  /** 清空队列并停止定时器，不触发 handler 和 onDrain */
  clear: () => void;
  /** 返回队列是否处于排空模式 */
  isDraining: () => boolean;
  /** 标记队列进入排空模式：队列继续按原节奏消费，消费完毕后触发 onDrain */
  startDrain: () => void;
}

/**
 * 创建一个消息队列，将高频的消息推送均匀化为固定间隔的消费节奏。
 *
 * 典型使用场景：SSE 流中多个 chunk 可能在同一个 TCP 包内到达，
 * 直接连续触发 onMessage 会导致 UI 文本"跳跃式"渲染。
 * 通过队列缓冲后按 interval 逐条消费，实现平滑的打字机效果。
 *
 * 生命周期：
 *   push → 入队 → 按 interval 逐条调用 handler
 *   startDrain → 标记排空模式 → 队列按原节奏消费完 → 触发 onDrain
 *   flush → 立即同步消费全部 → 触发 onDrain
 *   clear → 丢弃所有未处理消息，停止一切调度
 */
export function createMessageQueue<T>(options: MessageQueueOptions<T>): MessageQueue<T> {
  const { handler, interval = 50, onDrain } = options;
  const queue: T[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** 是否处于排空模式（SSE 流已结束，等待队列消费完毕） */
  let draining = false;

  /** 从队列取出一条消息处理，若队列非空则调度下一次；若队列已空且处于排空模式则触发 onDrain */
  function processNext() {
    if (queue.length === 0) {
      timer = null;
      // 队列已消费完毕，若处于排空模式则通知外部
      if (draining) {
        draining = false;
        onDrain?.();
      }
      return;
    }
    const item = queue.shift()!;
    handler(item);
    // 间隔 interval 后处理下一条，实现均匀消费节奏
    timer = setTimeout(processNext, interval);
  }

  function push(data: T) {
    queue.push(data);
    // 若当前没有定时器在运行（队列空闲），立即开始消费
    if (timer === null) {
      processNext();
    }
  }

  /** 立即同步消费所有剩余消息，用于需要瞬间完成的场景 */
  function flush() {
    draining = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    while (queue.length > 0) {
      handler(queue.shift()!);
    }
    draining = false;
    onDrain?.();
  }

  /** 清空队列，丢弃所有未处理消息（如用户主动中止时调用） */
  function clear() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    draining = false;
    queue.length = 0;
  }

  function isDraining() {
    return draining;
  }

  /**
   * 标记队列进入排空模式。
   * 队列会继续按原有 interval 节奏消费剩余消息，全部消费完后触发 onDrain。
   * 若队列已经为空，则立即触发 onDrain。
   */
  function startDrain() {
    draining = true;
    // 队列已经空了，直接触发
    if (queue.length === 0 && timer === null) {
      draining = false;
      onDrain?.();
    }
    // 否则等 processNext 自然消费完后触发 onDrain
  }

  return { push, flush, clear, isDraining, startDrain };
}
